<template>
  <v-layout class="restaurant-branches-container">
    <v-container fluid>
      <v-card>
        <v-card-title>
          <span>{{ $t('branches') }}</span>
          <v-spacer></v-spacer>
          <v-btn
            color="green"
            class="white--text"
            v-if="hasSettingsPermissions"
            @click="addNew"
            >{{ $t('branch.new') }}</v-btn
          >
        </v-card-title>
        <v-card-subtitle>
          {{ $t('branches.subtitle') }}
        </v-card-subtitle>
        <v-card-text>
          <template v-if="!branches.length">
            {{ $t('branches.empty') }}
          </template>
          <template v-else>
            <v-data-table
              :headers="headers"
              :items="branches"
              :items-per-page="50"
              :calculate-widths="true"
              :dense="false"
              :must-sort="true"
              sort-by="index"
              :sort-desc="true"
            >
              <template v-slot:item.actions="{ item }">
                <div class="children-inline">
                  <v-btn
                    icon
                    v-if="hasBranchPermissions"
                    @click="changeOrder(item, 1)"
                    ><v-icon>arrow_upward</v-icon></v-btn
                  >
                  <v-btn
                    icon
                    v-if="hasBranchPermissions"
                    @click="changeOrder(item, -1)"
                    ><v-icon>arrow_downward</v-icon></v-btn
                  >
                  <v-btn
                    icon
                    v-if="hasBranchPermissions"
                    :to="`/${restaurant.stringId}/branches/${item.id}`"
                    ><v-icon>edit</v-icon></v-btn
                  >
                  <v-switch
                    color="green"
                    class="ma-0"
                    v-model="item.active"
                    :hide-details="true"
                    v-if="hasBranchPermissions"
                    :disabled="item.active && activeCount === 1"
                    @click.capture.native="beforeChangeVisible($event, item)"
                    @change="changeVisible($event, item)"
                  ></v-switch>
                </div>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
      </v-card>
    </v-container>
  </v-layout>
</template>

<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import draggable from 'vuedraggable';
  import { IBranch } from '../../interfaces/IBranch';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import BranchesService from '../../api/services/branches.service';
  import RestaurantsService from '../../api/services/restaurants.service';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import { PermissionType } from '../../enums/PermissionType';
  import { Globals } from '../../modules/Globals';
  import { debounce } from '@/utils';

  @Component({
    components: { draggable },
  })
  export default class RestaurantBranches extends Vue {
    @Prop() restaurant: IRestaurant;
    branches: IBranch[] = [];
    branchesService = new BranchesService();
    restaurantsService = new RestaurantsService();
    headers = [];

    async created() {
      this.headers = [
        { text: this.$tc('description'), sortable: false, value: 'name' },
        { text: this.$tc('address'), sortable: false, value: 'address' },
        { text: '', value: 'actions', sortable: false, width: 200 },
      ];
    }

    mounted() {
      this.init();
    }

    async init() {
      await this.loadBranches();
    }

    get activeCount() {
      return this.branches.filter((value) => value.active).length;
    }

    get hasSettingsPermissions() {
      return vueData.hasPermission(PermissionType.StoreSettings);
    }

    get hasBranchPermissions() {
      return vueData.hasPermission(PermissionType.BranchSettings);
    }

    beforeChangeVisible(event, branch: IBranch) {
      if (!branch.active) return;
      const activeCount = this.branches.filter((value) => value.active).length;
      if (activeCount === 1) {
        vueData.addAlert(
          'Debe tener al menos una sucursal activa',
          AlertType.Error
        );
        event.stopPropagation();
        event.preventDefault();
      }
    }

    get maxBranches() {
      return 999;
    }

    addNew() {
      if (this.branches.length < this.maxBranches)
        this.$router.push(`/${this.restaurant.stringId}/branches/new`);
      else {
        vueData.addAlert(
          'Ha superado el máximo de sucursales de su plan',
          AlertType.Error
        );
      }
    }

    async changeVisible(newValue: boolean, branch: IBranch) {
      try {
        if (newValue) await this.branchesService.activate(branch);
        else await this.branchesService.deactivate(branch);

        const local = vueData.branches.find((b) => b.id === branch.id);
        if (local) local.active = newValue;
      } catch (e) {
        vueData.addAlert(
          this.$tc('errors.statusChangeFailed'),
          AlertType.Error
        );
        branch.active = !newValue;
      }
    }

    async loadBranches() {
      const branches = vueData.role.allBranches
        ? await this.branchesService.getAll(this.restaurant.id)
        : (
            await this.restaurantsService.getMember(
              this.restaurant.id,
              vueData.user.id
            )
          ).branches;
      this.branches = branches;
    }

    get sortedBranches() {
      // Sort by index DESC
      return [...this.branches].sort((a, b) => b.index - a.index);
    }

    saveIndexes = debounce(async (branches: IBranch[]) => {
      try {
        await BranchesService.saveIndexes({
          ids: branches.map((b) => b.id),
          indexes: branches.map((b) => b.index),
        });
        vueData.addAlert('Orden de sucursales guardado', AlertType.Success);
      } catch (e) {
        vueData.addAlert(
          'No se pudo guardar el orden de las sucursales',
          AlertType.Error
        );
      }
    }, 1000);

    changeOrder(branch: IBranch, direction: number) {
      const ensureOrder = () => {
        let order = this.sortedBranches.length;
        this.sortedBranches.forEach((b: IBranch) => {
          b.index = order;
          order--;
        });
      };

      ensureOrder();

      const currentArrayIndex = this.sortedBranches.findIndex(
        (b) => b.id === branch.id
      );
      if (
        currentArrayIndex === -1 ||
        (direction === -1 &&
          currentArrayIndex === this.sortedBranches.length - 1) ||
        (direction === 1 && currentArrayIndex === 0)
      )
        return;

      const newIndex = currentArrayIndex - direction;

      const targetItem = this.sortedBranches[newIndex];
      const targetItemIndex = targetItem.index;

      Vue.set(targetItem, 'index', branch.index);
      Vue.set(branch, 'index', targetItemIndex);

      this.saveIndexes(this.sortedBranches);
    }
  }
</script>
